import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Plus, X, CheckCircle2, Printer, Trash2 } from "lucide-react";
import { customApi, type ChartKind, type ChartSpec, type CustomListingDetail } from "../../lib/customListing";

// Custom Liste Studyosu 5. adim: kartelalar. Liste calismasinin istedigi kartelalar
// kodla cizilir (ucretsiz, yazilar dogru). Icerik calismadan gelir, burada duzenlenir.
// Kartela metni Etsy'de gorunur: Ingilizce.

const KIND_TR: Record<ChartKind, { title: string; hint: string }> = {
  design_numbers: { title: "Tasarım numaraları", hint: "Alıcı menüden tasarım seçiyor; her seçeneğin görseli numarasıyla gösterilir. Görselleri bu analizin tasarımlarından seç." },
  ink_colors: { title: "Mürekkep renkleri", hint: "Alıcı yazı/baskı rengini seçiyor; seçenekler renk yuvarlağıyla gösterilir." },
  font: { title: "Font seçenekleri", hint: "Alıcı font seçiyor; örnek değer her fontla basılır. Font adı Google Fonts'taki adıyla yazılır." },
  how_to_order: { title: "Nasıl sipariş verilir", hint: "Birden çok soru var; alıcıya sırayla ne yapacağı gösterilir." },
  photo_to_design: { title: "Fotoğraftan tişörte", hint: "Alıcının yükleyeceği türden gerçek bir fotoğraf → ok → tasarımda/tişörtte nasıl durduğu. \"Your photo\" satırına AI ile fotoğraf üret (~$0.03); \"Your shirt\" satırına örnek tasarım ya da basılan mockup seç. Fotoğraf üretilince basılan mockuplardan birine sağ alta \"CUSTOM PHOTO\" polaroidi eklenebilir (ücretsiz, ayrı görsel olur)." },
};

const inputCls = "rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200";

export function ChartsCard({ rec, onChanged }: { rec: CustomListingDetail; onChanged: () => void }) {
  const kinds = Object.keys(rec.chartDefaults ?? {}) as ChartKind[];
  if (!kinds.length) return null;
  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <h3 className="mb-1 text-sm font-semibold text-gray-900">Kartelalar</h3>
      <p className="mb-3 text-xs text-gray-500">Liste çalışması bu listede şu kartelaların gerektiğini söyledi. İçerik çalışmadan dolduruldu; düzenleyip çiz. Çizilen kartela liste görsellerinin sonuna eklenir, yeniden çizince yerini alır. Beden ve ürün rengi tabloları liste şablonundan gelir, burada çizilmez.</p>
      <div className="space-y-4">
        {kinds.map((k) => <ChartEditor key={k} rec={rec} kind={k} onChanged={onChanged} />)}
      </div>
    </div>
  );
}

/** "Fotograftan tisorte": Your photo satiri AI ile uretilir (yanindaki tasarimdan), Your shirt
 *  satiri ornek tasarimlardan / basilan mockuplardan / kolajdan secilir */
function PhotoPairPicker({ rec, label, value, pairDesign, onChange, onChanged }: { rec: CustomListingDetail; label: string; value?: string; pairDesign?: string; onChange: (u: string) => void; onChanged: () => void }) {
  const isPhoto = /photo/i.test(label);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const choices = [
    ...(rec.template.samples?.renders ?? []).map((r) => r.url),
    ...(rec.template.mockupRenders ?? []).map((r) => r.url),
    ...(rec.template.collage?.url ? [rec.template.collage.url] : []),
  ];
  const mockups = (rec.template.mockupRenders ?? []).filter((m) => !m.mockupId.endsWith(":photo"));
  if (isPhoto) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {value && <img src={value} alt="" className="h-8 w-8 rounded border border-gray-200 object-cover" />}
        <button onClick={async () => {
          if (!pairDesign) { setErr("Önce yanındaki \"Your shirt\" satırına tasarım seç"); return; }
          setBusy(true); setErr(null);
          try { const r = await customApi<{ url: string }>("POST", `/${rec.id}/photo-before`, { designUrl: pairDesign }); onChange(r.url); }
          catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
        }} disabled={busy} className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} {value ? "Yeniden üret" : "AI ile fotoğraf üret"}
        </button>
        {value && mockups.length > 0 && (
          <span className="inline-flex flex-wrap items-center gap-1 text-xs text-gray-500">
            Mockupa polaroid ekle:
            {mockups.map((m) => (
              <button key={m.mockupId} disabled={busy} title={`${m.label}: sağ alta "CUSTOM PHOTO" polaroidi + ok`} onClick={async () => {
                setBusy(true); setErr(null);
                try { await customApi("POST", `/${rec.id}/photo-inset`, { mockupId: m.mockupId, photoUrl: value }); onChanged(); }
                catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
              }} className="h-8 w-8 overflow-hidden rounded border-2 border-transparent bg-white hover:border-indigo-300 disabled:opacity-50">
                <img src={m.url} alt={m.label} loading="lazy" className="h-full w-full object-cover" />
              </button>
            ))}
          </span>
        )}
        {err && <span className="text-xs text-red-600">{err}</span>}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {choices.slice(0, 14).map((u) => (
        <button key={u} onClick={() => onChange(u)} aria-pressed={value === u} title="Bu satırın görseli"
          className={`h-8 w-8 overflow-hidden rounded border-2 bg-white ${value === u ? "border-indigo-500" : "border-transparent hover:border-gray-300"}`}>
          <img src={u} alt="" loading="lazy" className="h-full w-full object-cover" />
        </button>
      ))}
      {choices.length === 0 && <span className="text-xs text-gray-500">Önce örnek bas</span>}
    </div>
  );
}

function ChartEditor({ rec, kind, onChanged }: { rec: CustomListingDetail; kind: ChartKind; onChanged: () => void }) {
  const made = rec.template.charts?.[kind];
  const [spec, setSpec] = useState<ChartSpec>(() => made?.spec ?? rec.chartDefaults[kind]!);
  const [err, setErr] = useState<string | null>(null);
  const draw = useMutation({
    mutationFn: (next?: ChartSpec) => customApi("POST", `/${rec.id}/charts`, { kind, spec: next ?? spec }),
    onSuccess: () => { setErr(null); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  // Calismanin istedigi kartela henuz cizilmediyse kendiliginden cizilir (kodla, ucretsiz);
  // elle silindiyse dokunulmaz
  const auto = useRef(false);
  useEffect(() => {
    if (auto.current || made || rec.template.chartsRemoved?.[kind] || !spec?.items?.some((i) => i.label.trim()) || kind === "photo_to_design" || kind === "how_to_order") return;
    auto.current = true;
    draw.mutate(undefined);
  }, [made, kind]); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = useMutation({
    mutationFn: () => customApi("DELETE", `/${rec.id}/charts/${kind}`),
    onSuccess: () => { setErr(null); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  const setItem = (i: number, patch: Partial<ChartSpec["items"][number]>) =>
    setSpec((s) => ({ ...s, items: s.items.map((it, k) => (k === i ? { ...it, ...patch } : it)) }));
  const add = () => setSpec((s) => ({
    ...s, items: [...s.items, kind === "font" ? { label: `Font ${s.items.length + 1}`, font: "" } : kind === "design_numbers" ? { label: `Design ${s.items.length + 1}` } : { label: "" }],
  }));
  const id = `ch-${kind}`;

  return (
    <div className="rounded-lg bg-gray-50 p-3">
      <div className="mb-1 flex items-center gap-2">
        <p className="text-sm font-medium text-gray-900">{KIND_TR[kind].title}</p>
        {made && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Çizildi</span>}
      </div>
      <p className="mb-2 text-xs text-gray-500">{KIND_TR[kind].hint}</p>
      <div className="grid gap-4 lg:grid-cols-[1fr_220px]">
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <label htmlFor={`${id}-title`} className="sr-only">Kartela başlığı</label>
            <input id={`${id}-title`} value={spec.title} onChange={(e) => setSpec((s) => ({ ...s, title: e.target.value }))} maxLength={60} placeholder="Başlık (İngilizce)" className={`${inputCls} min-w-0 flex-1`} />
            <label htmlFor={`${id}-bg`} className="sr-only">Zemin</label>
            <select id={`${id}-bg`} value={spec.background ?? "collage"} onChange={(e) => setSpec((s) => ({ ...s, background: e.target.value as "collage" | "plain" }))} className={`${inputCls} w-40`}>
              <option value="collage">Kolaj zeminli</option>
              <option value="plain">Düz zemin</option>
            </select>
            <input type="color" aria-label="Vurgu rengi" title="Başlık vurgu rengi" value={spec.accent ?? "#b3263a"} onChange={(e) => setSpec((s) => ({ ...s, accent: e.target.value }))}
              className="h-9 w-10 cursor-pointer rounded border border-gray-200 bg-white" />
            {kind !== "how_to_order" && kind !== "photo_to_design" && (
              <>
                <label htmlFor={`${id}-cols`} className="sr-only">Dizilim</label>
                <select id={`${id}-cols`} value={spec.cols ?? 0} onChange={(e) => setSpec((s) => ({ ...s, cols: Number(e.target.value) || undefined }))} title="Dizilim" className={`${inputCls} w-40`}>
                  <option value={0}>Otomatik dizilim</option>
                  {[1, 2, 3, 4, 5, 6].map((c) => <option key={c} value={c}>{c} sütun</option>)}
                </select>
              </>
            )}
            {kind === "design_numbers" && (
              <>
                <label htmlFor={`${id}-tile`} className="sr-only">Kutu stili</label>
                <select id={`${id}-tile`} value={spec.tile ?? "box"} onChange={(e) => setSpec((s) => ({ ...s, tile: e.target.value as ChartSpec["tile"] }))} className={`${inputCls} w-32`}>
                  <option value="box">Kutulu</option>
                  <option value="round">Yuvarlak</option>
                  <option value="none">Kutusuz</option>
                </select>
                <label htmlFor={`${id}-mono`} className="sr-only">Görsel rengi</label>
                <select id={`${id}-mono`} value={spec.mono ? "mono" : "color"} onChange={(e) => setSpec((s) => ({ ...s, mono: e.target.value === "mono" }))} className={`${inputCls} w-32`}>
                  <option value="color">Renkli</option>
                  <option value="mono">Siyah-beyaz</option>
                </select>
              </>
            )}
            {kind === "font" && (
              <>
                <label htmlFor={`${id}-sample`} className="sr-only">Örnek yazı</label>
                <input id={`${id}-sample`} value={spec.sample ?? ""} onChange={(e) => setSpec((s) => ({ ...s, sample: e.target.value }))} maxLength={40} placeholder="Örnek yazı" className={`${inputCls} w-44`} />
              </>
            )}
          </div>
          {spec.items.map((it, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <span className="w-5 text-right text-xs text-gray-400">{i + 1}</span>
              <input aria-label={`${i + 1}. satır etiketi`} value={it.label} onChange={(e) => setItem(i, { label: e.target.value })} maxLength={120}
                className={`${inputCls} min-w-0 ${kind === "how_to_order" ? "flex-1" : "w-36"}`} />
              {kind === "ink_colors" && (
                <input type="color" aria-label={`${it.label || i + 1} rengi`} value={it.hex ?? "#cccccc"} onChange={(e) => setItem(i, { hex: e.target.value })}
                  className="h-8 w-10 cursor-pointer rounded border border-gray-200 bg-white" />
              )}
              {kind === "font" && (
                <input aria-label={`${i + 1}. font adı`} value={it.font ?? ""} onChange={(e) => setItem(i, { font: e.target.value })} maxLength={60} placeholder="Google font adı"
                  className={`${inputCls} w-44`} />
              )}
              {kind === "photo_to_design" && (
                <PhotoPairPicker rec={rec} label={it.label} value={it.imageUrl} pairDesign={spec.items[i + 1]?.imageUrl} onChange={(u) => {
                  // Uretilen fotograf hemen kaydedilsin (cizilmeden sayfadan cikilirsa ucret bosa gidiyordu)
                  const next = { ...spec, items: spec.items.map((x, k) => (k === i ? { ...x, imageUrl: u } : x)) };
                  setSpec(next);
                  if (/photo/i.test(it.label)) draw.mutate(next);
                }} onChanged={onChanged} />
              )}
              {kind === "design_numbers" && (
                <div className="flex flex-wrap gap-1">
                  {/* Ikon seti (4. adim) varsa satirin kendi ikonu once */}
                  {[...new Set([rec.template.iconSet?.icons?.[i]?.url, ...(rec.template.samples?.renders ?? []).map((r) => r.url).slice(0, 12), ...rec.designImages.slice(0, 12)].filter((u): u is string => !!u))].map((u) => (
                    <button key={u} onClick={() => setItem(i, { imageUrl: u })} aria-pressed={it.imageUrl === u} title="Bu satırın görseli"
                      className={`h-8 w-8 overflow-hidden rounded border-2 bg-white ${it.imageUrl === u ? "border-indigo-500" : "border-transparent hover:border-gray-300"}`}>
                      <img src={u} alt="" loading="lazy" className="h-full w-full object-contain" />
                    </button>
                  ))}
                  {rec.designImages.length === 0 && !rec.template.iconSet?.icons?.[i]?.url && <span className="text-xs text-gray-500">Bu analizde tasarım yok</span>}
                </div>
              )}
              <button onClick={() => setSpec((s) => ({ ...s, items: s.items.filter((_, k) => k !== i) }))} aria-label={`${i + 1}. satırı sil`}
                className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"><X className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {spec.items.length < 30 && (
              <button onClick={add} className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"><Plus className="h-3.5 w-3.5" /> Satır ekle</button>
            )}
            <button onClick={() => draw.mutate(undefined)} disabled={draw.isPending || !spec.items.some((i) => i.label.trim())}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
              {draw.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Printer className="h-3.5 w-3.5" />} {made ? "Yeniden çiz" : "Kartelayı çiz"}
            </button>
            {made && rec.chartDefaults[kind] && (
              <button onClick={() => { const d = rec.chartDefaults[kind]!; setSpec(d); draw.mutate(d); }} disabled={draw.isPending}
                title="Kartelayı çalışmadan (ve ikon setinden) yeniden doldurup çizer; elle yaptığın değişiklikler gider"
                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                Varsayılana dön
              </button>
            )}
            {made && (
              <button onClick={() => { if (confirm("Çizilen kartela silinsin mi? Liste görsellerinden de çıkar.")) remove.mutate(); }} disabled={remove.isPending}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
                {remove.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Kartelayı sil
              </button>
            )}
          </div>
          {err && <p className="text-sm text-red-600">{err}</p>}
        </div>
        {made?.url && (
          <a href={made.url} target="_blank" rel="noopener noreferrer" title="Tam boyutta aç">
            <img src={made.url} alt={KIND_TR[kind].title} className="w-full rounded-lg border border-gray-200 bg-white" />
          </a>
        )}
      </div>
    </div>
  );
}
